import { useMemo, useState } from 'react';

function Ring({ pct, color, size = 80, stroke = 8 }) {
  const r = (size - stroke) / 2;
  const circ = 2 * Math.PI * r;
  const dash = Math.min(1, pct / 100) * circ;
  return (
    <svg width={size} height={size} style={{ transform: 'rotate(-90deg)' }}>
      <circle cx={size/2} cy={size/2} r={r} fill="none" stroke="#f0f0f0" strokeWidth={stroke} />
      <circle cx={size/2} cy={size/2} r={r} fill="none" stroke={color} strokeWidth={stroke}
        strokeDasharray={`${dash} ${circ}`} strokeLinecap="round" style={{ transition: 'stroke-dasharray 0.5s' }} />
    </svg>
  );
}

const DEFAULT_TARGETS = { daily: 5, monthly: 80 };
const TARGET_KEY = 'vbot_lead_targets_v1';
const loadTargets = () => { try { return { ...DEFAULT_TARGETS, ...JSON.parse(localStorage.getItem(TARGET_KEY) || '{}') }; } catch (e) { return { ...DEFAULT_TARGETS }; } };
const saveTargets = (t) => { try { localStorage.setItem(TARGET_KEY, JSON.stringify(t)); } catch (e) {} };

export function LeadTargetWidget({ leads, agentId }) {
  const [period, setPeriod] = useState('daily');
  const [targets, setTargets] = useState(loadTargets);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const startEdit = () => { setDraft(String(targets[period])); setEditing(true); };
  const commitEdit = () => {
    const n = parseInt(draft, 10);
    if (n > 0) { const next = { ...targets, [period]: n }; setTargets(next); saveTargets(next); }
    setEditing(false);
  };

  const achieved = useMemo(() => {
    const now = new Date();
    if (period === 'daily') {
      return leads.filter(l => {
        const d = new Date(l.created_at);
        return d.toDateString() === now.toDateString() &&
          (!agentId || String(l.assigned_agent_id) === String(agentId));
      }).length;
    } else {
      return leads.filter(l => {
        const d = new Date(l.created_at);
        return d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear() &&
          (!agentId || String(l.assigned_agent_id) === String(agentId));
      }).length;
    }
  }, [leads, period, agentId]);

  const converted = useMemo(() => {
    const now = new Date();
    return leads.filter(l => {
      if (l.status !== 'converted') return false;
      if (agentId && String(l.assigned_agent_id) !== String(agentId)) return false;
      const d = new Date(l.created_at);
      if (period === 'daily') return d.toDateString() === now.toDateString();
      return d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear();
    }).length;
  }, [leads, period, agentId]);

  const target = targets[period];
  const pct = Math.round((achieved / Math.max(1, target)) * 100);
  const convPct = achieved ? Math.round((converted / achieved) * 100) : 0;
  const color = pct >= 100 ? '#25d366' : pct >= 60 ? '#ff9800' : '#f44336';

  return (
    <div style={{ background: '#fff', borderRadius: 14, border: '1px solid #f0f0f0', padding: '18px 20px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <div style={{ fontSize: 13, fontWeight: 800, color: '#1a1a2e' }}>🎯 Target Tracker</div>
        <div style={{ display: 'flex', gap: 6 }}>
          {['daily', 'monthly'].map(p => (
            <button key={p} onClick={() => setPeriod(p)} style={{
              padding: '4px 12px', fontSize: 11, fontWeight: 700, border: 'none', borderRadius: 20, cursor: 'pointer', fontFamily: 'inherit',
              background: period === p ? '#075e54' : '#f0f0f0', color: period === p ? '#fff' : '#555',
            }}>{p === 'daily' ? 'Today' : 'Month'}</button>
          ))}
        </div>
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 20 }}>
        <div style={{ position: 'relative', flexShrink: 0 }}>
          <Ring pct={pct} color={color} size={90} stroke={9} />
          <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
            <div style={{ fontSize: 18, fontWeight: 800, color }}>{pct}%</div>
          </div>
        </div>

        <div style={{ flex: 1 }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            {[
              { label: 'Achieved', value: achieved, color: '#1a1a2e' },
              { label: 'Target', color: '#aaa', value: editing
                ? <input autoFocus type="number" min="1" value={draft} onChange={e => setDraft(e.target.value)} onBlur={commitEdit} onKeyDown={e => e.key === 'Enter' && commitEdit()} style={{ width: 60, fontSize: 14, fontWeight: 800, border: '1px solid #ddd', borderRadius: 6, padding: '2px 6px' }} />
                : <span onClick={startEdit} title="Click to change target" style={{ cursor: 'pointer', borderBottom: '1px dashed #bbb' }}>{target}</span> },
              { label: 'Converted', value: converted, color: '#085041' },
              { label: 'Conv %', value: convPct + '%', color: '#25d366' },
            ].map(c => (
              <div key={c.label} style={{ background: '#fafafa', borderRadius: 8, padding: '8px 12px' }}>
                <div style={{ fontSize: 16, fontWeight: 800, color: c.color }}>{c.value}</div>
                <div style={{ fontSize: 10, color: '#aaa', marginTop: 1 }}>{c.label}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {pct >= 100 && (
        <div style={{ marginTop: 12, padding: '8px 14px', background: '#e8f5e9', borderRadius: 8, fontSize: 12, color: '#2e7d32', fontWeight: 700, textAlign: 'center' }}>
          🎉 Target achieved! Great work!
        </div>
      )}
      {pct < 100 && (
        <div style={{ marginTop: 12, padding: '8px 14px', background: '#f5f5f5', borderRadius: 8, fontSize: 12, color: '#888', textAlign: 'center' }}>
          {Math.max(0, target - achieved)} more {period === 'daily' ? 'today' : 'this month'} to hit target
          <span style={{ fontSize: 10, color: '#bbb', display: 'block', marginTop: 2 }}>Tip: click the Target number to set your own</span>
        </div>
      )}
    </div>
  );
}
